import * as React from 'react';

import { ComposeIcon, EmailIcon, MoreActionsIcon, SendIcon, TrashIcon } from '@coursera/cds-icons';
import { DropDown } from '@coursera/coursera-ui';

import type { PrimaryFilterType } from 'bundles/enterprise-admin-user-filters/types';
import { Button } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/enterprise-admin-users';

import 'css!./__styles__/TableRowActions';

type Props = {
  primaryFilterType?: PrimaryFilterType;
  userIdentifier: string;
  allowDeletion: boolean;
  allowSendMessage: boolean;
  disableEditProfile?: boolean;
  onSendMessage: (id: string) => void;
  onEditProfile: (userIdentifier: string) => void;
  onRemoveInvitation: () => void;
  onRemoveLearner: () => void;
  onAssignContent?: () => void;
};

type State = {
  anchorElement?: HTMLElement;
};

class TableRowActions extends React.Component<Props, State> {
  state: State = {};

  showDropdown = (e?: React.MouseEvent<HTMLButtonElement>) => {
    this.setState({
      anchorElement: e?.currentTarget,
    });
  };

  hideDropdown = () => {
    this.setState({ anchorElement: undefined });
  };

  sendMessage = () => {
    const { userIdentifier, onSendMessage } = this.props;
    onSendMessage(userIdentifier);
    this.hideDropdown();
  };

  onRemoveLearner = () => {
    this.props.onRemoveLearner();
    this.hideDropdown();
  };

  onRemoveInvitation = () => {
    this.props.onRemoveInvitation();
    this.hideDropdown();
  };

  renderLabel = (svgElement: React.ReactNode, text: string) => (
    <div className="horizontal-box">
      {svgElement}
      <p className="m-l-1 m-b-0">{text}</p>
    </div>
  );

  openEditorModal = () => {
    const { onEditProfile, userIdentifier } = this.props;
    onEditProfile(userIdentifier);
    this.hideDropdown();
  };

  render() {
    const { anchorElement } = this.state;
    const { allowDeletion, allowSendMessage, primaryFilterType, disableEditProfile, onAssignContent } = this.props;
    return (
      <div data-pendo-guide="UsersListRowAction" className="rc-TableRowActions">
        <Button onClick={this.showDropdown} variant="ghost" icon={<MoreActionsIcon />} style={{ marginTop: -12 }}>
          {_t('Actions')}
        </Button>
        {anchorElement && (
          <DropDown.Menu
            isOpen={!!anchorElement}
            anchorElement={anchorElement}
            onRequestClose={this.hideDropdown}
            dropDownPosition={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <div className="c-dropdownItemWrapper">
              {allowSendMessage && (
                <DropDown.Item
                  key="sendMessage"
                  label={this.renderLabel(<EmailIcon />, _t('Send message'))}
                  onClick={this.sendMessage}
                />
              )}
              {!disableEditProfile && (
                <DropDown.Item
                  key="editLearner"
                  label={this.renderLabel(<ComposeIcon />, _t('Edit profile'))}
                  onClick={this.openEditorModal}
                  htmlAttributes={{ 'data-e2e': 'table-row-edit-learner' }}
                />
              )}
              {allowDeletion && primaryFilterType === 'INVITED' && (
                <DropDown.Item
                  key="deleteInvitation"
                  label={this.renderLabel(<TrashIcon />, _t('Remove invitation'))}
                  onClick={this.onRemoveInvitation}
                  htmlAttributes={{ 'data-e2e': 'table-row-remove-invitation' }}
                />
              )}
              {allowDeletion && primaryFilterType === 'MEMBER' && (
                <DropDown.Item
                  key="deleteLearner"
                  label={this.renderLabel(<TrashIcon />, _t('Remove learner'))}
                  onClick={this.onRemoveLearner}
                  htmlAttributes={{ 'data-e2e': 'table-row-remove-learner' }}
                />
              )}
              {onAssignContent && primaryFilterType === 'MEMBER' && (
                <DropDown.Item
                  key="assignContent"
                  label={this.renderLabel(<SendIcon />, _t('Assign content'))}
                  onClick={onAssignContent}
                  htmlAttributes={{ 'data-e2e': 'table-row-assign-content' }}
                />
              )}
            </div>
          </DropDown.Menu>
        )}
      </div>
    );
  }
}

export default TableRowActions;
